/**
 * Activation-aware locked overlay — web port of iOS
 * `client-native/.../feed/components/FeedLockedOverlay.tsx`.
 *
 * Driven by the shared activation journey (`@/features/activation`): the feed
 * unlocks once the user advances PAST the step that declares `unlocks: "feed"`
 * (step 4, "Complete 8 lessons"). While locked it shows:
 *   - blurred dark backdrop + gradient
 *   - a 3D padlock
 *   - "Reach Level {N} to unlock the Feed" (N in Parrot blue)
 *   - the user's ACTIVE step label + count + a blue thermometer progress bar
 *   - a "Keep Learning" / "Review" CTA
 *
 * On first unlock it plays the celebration (bar fills 0→100%, subtitle swaps to
 * "Feed Unlocked!", confetti, then the overlay dissolves), gating "seen" in
 * localStorage so it only plays once.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThermometerProgressBar,
  Confetti,
  colors,
} from "@/ui";
import {
  getFeatureUnlockLevel,
  type ActivationJourneyState,
} from "@/features/activation";
import feedLockedBg from "@/assets/feed-locked-bg.jpg";
import lockImage from "@/assets/lock.png";

const UNLOCK_SEEN_KEY = "feed_unlock_celebration_seen";

// iOS FeedLockedOverlay.tsx:41-42 — hold after the bar fills, then dissolve.
const CELEBRATION_HOLD_MS = 550;
const CELEBRATION_DISSOLVE_MS = 650;

/** Short, sentence-cased label for the active step (mirrors getShortFeedLockedOverlayStepLabel). */
function shortStepLabel(check: string | undefined): string {
  switch (check) {
    case "learningWords":
    case "totalWords":
      return "Save words";
    case "reviewedCards":
      return "Complete review";
    case "knownWords":
      return "Master words";
    case "completedLessons":
      return "Complete lessons";
    case "phrases":
      return "Highlight phrases";
    case "listeningTime":
      return "Listen";
    case "openedFeed":
      return "Open the feed";
    default:
      return "Keep learning";
  }
}

/** Right-aligned "{cur}/{goal} {unit}" (mirrors formatActivationProgress). */
function formatProgress(check: string | undefined, cur: number, goal: number): string {
  let unit = "";
  switch (check) {
    case "completedLessons":
      unit = "lessons";
      break;
    case "phrases":
      unit = "phrases";
      break;
    case "reviewedCards":
      unit = "reviews";
      break;
    case "knownWords":
      unit = "mastered";
      break;
    case "learningWords":
    case "totalWords":
      unit = "words";
      break;
    case "openedFeed":
      // Single binary milestone (goal 1) — iOS shows a state word, not "0/1".
      return cur >= goal ? "Done" : "Not yet";
    case "listeningTime": {
      // listening goals are in seconds; show minutes.
      const curMin = Math.floor(cur / 60);
      const goalMin = Math.floor(goal / 60);
      return `${curMin}/${goalMin}m`;
    }
    default:
      unit = "";
  }
  return `${cur}/${goal}${unit ? ` ${unit}` : ""}`;
}

interface FeedLockedOverlayProps {
  journey: ActivationJourneyState;
  /** Fired after the unlock celebration finishes and the overlay dissolves. */
  onCelebrationComplete?: () => void;
}

export function FeedLockedOverlay({
  journey,
  onCelebrationComplete,
}: FeedLockedOverlayProps) {
  const navigate = useNavigate();
  // Defensive fallback only — getFeatureUnlockLevel("feed") maps cleanly today
  // (step 4 / "Complete 8 lessons" → level 5), so this 5 should never be used.
  const unlockLevel = getFeatureUnlockLevel("feed") ?? 5;

  const step = journey.stepConfig;
  const stepCheck = step?.check;
  const curValue = (() => {
    if (!step) return 0;
    return Math.round((journey.currentProgress ?? 0) * step.goal);
  })();
  const goalValue = step?.goal ?? 1;
  const progress = journey.currentProgress ?? 0;

  // --- Unlock celebration: when the journey reports the feed just unlocked and
  // we haven't shown the celebration yet, fill the bar, swap copy, fire
  // confetti, then dissolve.
  const seen = useRef(localStorage.getItem(UNLOCK_SEEN_KEY) === "true");
  const [celebrating, setCelebrating] = useState(false);
  // Subtitle swaps to "Feed Unlocked!" only once the bar finishes filling
  // (iOS setIsUnlocked inside triggerDissolve), not the moment we celebrate.
  const [unlocked, setUnlocked] = useState(false);
  const [dissolving, setDissolving] = useState(false);

  // `journey.isActivationComplete`/unlock is the upstream gate; FeedPage only
  // mounts this overlay while LOCKED, so a transition to unlocked is detected by
  // the parent. Here we expose the celebration when explicitly told (via re-mount
  // with celebrate=true). For simplicity the parent decides lock vs unlock; this
  // component only animates the celebration when `onCelebrationComplete` exists
  // AND it hasn't been seen — i.e. the parent mounts us one last time on unlock.
  //
  // IMPORTANT: the parent (`FeedPage`) passes a fresh `() => setCelebrating(false)`
  // closure every render, so `onCelebrationComplete` cannot be an effect dep — a
  // new identity would tear down the timers mid-celebration, re-run the effect,
  // hit the `seen.current` guard, and never reschedule `doneTimer` → the overlay
  // wedges on "Feed Unlocked!" forever and the real feed never appears. We mirror
  // iOS (CloudflarePlayer/FeedLockedOverlay): hold the callback in a ref updated
  // each render, depend on a STABLE boolean, and add a safety-net fallback so the
  // overlay always dissolves even if the primary timer is interrupted.
  const completeRef = useRef(onCelebrationComplete);
  completeRef.current = onCelebrationComplete;
  const isCelebratingMode = onCelebrationComplete !== undefined;

  // iOS triggerDissolve (FeedLockedOverlay.tsx:141-174): fired by the bar's
  // onFillComplete — subtitle swaps, hold 550ms, fade 650ms, then complete.
  const unlockedRef = useRef(false);
  const dissolveTimersRef = useRef<number[]>([]);
  const triggerDissolve = useCallback(() => {
    if (!completeRef.current || unlockedRef.current) return;
    unlockedRef.current = true;
    setUnlocked(true);
    dissolveTimersRef.current.push(
      window.setTimeout(() => {
        setDissolving(true);
        dissolveTimersRef.current.push(
          window.setTimeout(
            () => completeRef.current?.(),
            CELEBRATION_DISSOLVE_MS,
          ),
        );
      }, CELEBRATION_HOLD_MS),
    );
  }, []);
  useEffect(
    () => () => {
      dissolveTimersRef.current.forEach((t) => clearTimeout(t));
    },
    [],
  );

  useEffect(() => {
    if (!isCelebratingMode || seen.current) return;
    setCelebrating(true);
    localStorage.setItem(UNLOCK_SEEN_KEY, "true");
    seen.current = true;
    // Safety net (iOS parity): if the bar's onFillComplete never fires, still
    // resolve the celebration so the user is never stranded on the feed.
    const safetyTimer = setTimeout(() => triggerDissolve(), 4500);
    return () => clearTimeout(safetyTimer);
  }, [isCelebratingMode, triggerDissolve]);

  const cta = stepCheck === "reviewedCards"
    ? { label: "Review", to: "/home/dictionary?highlightReview=true" }
    : { label: "Keep Learning", to: "/home/learn" };

  return (
    <div
      className="absolute inset-0 z-50 flex justify-center overflow-hidden bg-black transition-opacity duration-[650ms]"
      style={{ opacity: dissolving ? 0 : 1 }}
    >
      {/* Backdrop + content constrained to the same centered 600px column the
          other tabs use (.appPadding), so wide screens get a narrow panel on
          black instead of a viewport-wide stretched photo. */}
      <div className="relative w-full max-w-[600px] h-full flex flex-col items-center justify-center px-8 overflow-hidden">
      {/* Blurred photo backdrop */}
      <img
        src={feedLockedBg}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(3px)" }}
      />
      {/* 4-stop dark gradient (rgba(0,0,0,.9)→.5→.35→.9) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 10%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.9) 100%)",
        }}
      />

      <Confetti active={celebrating} />

      <div className="relative z-10 flex flex-col items-center text-center max-w-[320px] w-full">
        {/* 3D padlock (iOS @assets/lock.png at 110x110 with a soft drop shadow) */}
        <img
          src={lockImage}
          alt=""
          aria-hidden
          className="mb-6"
          style={{
            width: 110,
            height: 110,
            objectFit: "contain",
            filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.35))",
          }}
        />

        {unlocked ? (
          <h2 className="text-2xl font-bold leading-tight mb-8" style={{ color: colors.blue }}>
            Feed Unlocked!
          </h2>
        ) : (
          <h2 className="text-white text-[28px] font-semibold leading-tight mb-8">
            Reach{" "}
            <span className="font-bold" style={{ color: colors.blue }}>
              Level {unlockLevel}
            </span>{" "}
            to unlock the Feed
          </h2>
        )}

        {/* Progress block */}
        <div className="w-full">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-xs font-semibold">
              {shortStepLabel(stepCheck)}
            </span>
            <span className="text-white text-xs font-semibold">
              {formatProgress(stepCheck, curValue, goalValue)}
            </span>
          </div>
          {/* iOS FeedLockedOverlay.tsx:276-284: blue fill on a white
              translucent track; onFillComplete drives the dissolve. */}
          <ThermometerProgressBar
            progress={celebrating ? 1 : progress}
            fillColor={colors.blue}
            trackColor="rgba(255, 255, 255, 0.22)"
            stripeColor="rgba(255, 255, 255, 0.18)"
            onFillComplete={celebrating ? triggerDissolve : undefined}
          />
        </div>
      </div>

      {/* Bottom CTA */}
      {!celebrating && (
        <div className="absolute bottom-24 left-0 right-0 flex justify-center px-8">
          <button
            type="button"
            onClick={() => navigate(cta.to)}
            className="w-full max-w-[360px] flex items-center justify-center py-3.5 rounded-full text-white text-sm font-bold active:scale-95 transition-transform"
            style={{ backgroundColor: colors.blue }}
          >
            {cta.label}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

export { UNLOCK_SEEN_KEY };
